import * as React from "react";
import { observer } from "mobx-react";

import { useLocalization } from "@shared-ui/localization-context";

import { UitkFigure, UitkFigureAspectRatioType } from "uitk-react-images";
import { UitkScrim, UitkScrimType } from "uitk-react-scrim";
import { UitkCard, UitkCardLink } from "uitk-react-cards";
import { UitkLayoutFlex, UitkLayoutFlexItem } from "uitk-react-layout-flex";
import { UitkHeading, UitkSubheading } from "uitk-react-text";

import { DestinationSuggestionsProps } from "../typings";
import {
  Destination,
  DestinationSuggestionsFlexModuleResult,
} from "typings/microserviceModels/destination-suggestions-flex-module";
import { ItemKeyHelper } from "components/utility/ItemKeyHelper";
import { IsomorphicCarousel } from "components/shared/IsomorphicCarousel/IsomorphicCarousel";
import { Action } from "components/utility/analytics/FlexAnalyticsUtils";
import { TrackedLink } from "components/utility/analytics/TrackedLink";
import { BlossomImage } from "components/shared/BlossomImage/BlossomImage";

import { withMabTracker, MabTrackEvent } from "components/utility/analytics/MabTracker";
import {
  PageBackgroundContext,
  BackgroundColorTokens,
} from "components/flexComponents/PageLevelProviders/PageBackgroundColor/PageBackgroundContext";
import { withStores } from "stores";
import { getFmId } from "src/stores/ExperienceTemplateStore";
import { CarouselPeekWidth } from "src/components/shared/IsomorphicCarousel/utils/constants";

const EmbeddingCards = withMabTracker(
  ({
    destination,
    cardIndex,
    mabTrack,
  }: {
    destination: Destination;
    cardIndex: number;
    mabTrack?: (x: MabTrackEvent) => null;
  }) => {
    const rfrrId = cardIndex + 1;

    const cardClicked = mabTrack
      ? () => {
          const mabTrackData: MabTrackEvent = {
            moduleName: "embedding",
            eventTarget: "embedding",
            eventType: "homepageClick.nonPath",
            rfrr: rfrrId.toString(),
          };
          mabTrack(mabTrackData);
        }
      : undefined;

    const backgroundColor = React.useContext(PageBackgroundContext);
    const withBorder = backgroundColor === BackgroundColorTokens.BACKGROUND_WHITE;
    const { destinationImg, imgAlt, name, url } = destination;

    return (
      <UitkCard border={withBorder}>
        <UitkFigure ratio={UitkFigureAspectRatioType.R3_2}>
          <BlossomImage src={destinationImg} alt={imgAlt} />
          <UitkScrim type={UitkScrimType.BOTTOM}>
            <UitkLayoutFlex justifyContent="space-between">
              <UitkLayoutFlexItem>
                <div>
                  <UitkHeading tag="h3" size={5} className="cardHeading">
                    {name}
                  </UitkHeading>
                </div>
              </UitkLayoutFlexItem>
            </UitkLayoutFlex>
          </UitkScrim>
        </UitkFigure>
        <UitkCardLink>
          <TrackedLink moduleName="Embedding" action={Action.CLICK} href={url} rfrr={rfrrId} onClick={cardClicked}>
            {name}
          </TrackedLink>
        </UitkCardLink>
      </UitkCard>
    );
  }
);

export const Embedding = withStores("flexModuleModelStore")(
  observer((props: DestinationSuggestionsProps) => {
    const { templateComponent, flexModuleModelStore } = props;

    if (!templateComponent || !flexModuleModelStore) {
      return null;
    }

    const { metadata } = templateComponent;
    const { id } = metadata;
    const model = flexModuleModelStore.getModel(id) as DestinationSuggestionsFlexModuleResult | null;

    /* istanbul ignore next */
    if (!model) {
      return null;
    }

    const keyHelper = new ItemKeyHelper(id);
    const { formatText } = useLocalization();
    const title = formatText("destinationSuggestions.embedding.title.v2");
    const subtitle = formatText("destinationSuggestions.embedding.title");
    const fmId = getFmId(templateComponent);

    return (
      <div
        data-testid="embedding"
        className="DestinationSuggestions uitk-cell all-b-margin-three"
        id={id}
        data-fm={fmId}
      >
        <div className="s-l-padding-three m-l-padding-three all-y-padding-three">
          <UitkHeading tag="h2" size={3}>
            {title}
          </UitkHeading>
          <UitkSubheading tag="h3">{subtitle}</UitkSubheading>
        </div>
        <IsomorphicCarousel
          className="carousel-component"
          itemsVisible={{
            lg: 3,
            md: 2,
            sm: 1,
          }}
          buttonText={{
            nextButton: formatText("carousel.item.next"),
            prevButton: formatText("carousel.item.prev"),
          }}
          pageBy={1}
          peekWidth={CarouselPeekWidth.LARGE}
        >
          {model.destinations.map((destination, index) => (
            <EmbeddingCards
              key={keyHelper.next()}
              destination={destination}
              cardIndex={index}
              mabTrack={props.mabTrack}
            />
          ))}
        </IsomorphicCarousel>
      </div>
    );
  })
);
(Embedding as React.FC).displayName = "EmbeddingView";
